// src/taskpane/components/Settings.tsx
import * as React from "react";
import {
  Button,
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { Dismiss24Regular, Settings24Regular } from "@fluentui/react-icons";
import { AddinSettings } from "../services/types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  dialog: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "24px",
    width: "90%",
    maxWidth: "420px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    margin: 0,
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    marginTop: "20px",
  },
});

interface SettingsProps {
  settings: AddinSettings;
  onSave: (settings: AddinSettings) => void;
}

const SETTINGS_KEY = "ppt-translator-settings";

export function loadSettings(): AddinSettings {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // 忽略解析错误
  }
  return {
    apiKey: "",
    apiRegion: "global",
    defaultTargetLanguage: "zh-Hans",
  };
}

export function saveSettingsToStorage(settings: AddinSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "6px 10px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  fontSize: "14px",
  boxSizing: "border-box",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "6px 10px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  fontSize: "14px",
  backgroundColor: "#fff",
  cursor: "pointer",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "14px",
  fontWeight: 600,
  marginBottom: "4px",
};

export default function Settings({ settings, onSave }: SettingsProps) {
  const styles = useStyles();
  const [open, setOpen] = React.useState(false);
  const [localSettings, setLocalSettings] =
    React.useState<AddinSettings>(settings);
  const [saved, setSaved] = React.useState(false);

  const handleSave = () => {
    saveSettingsToStorage(localSettings);
    onSave(localSettings);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      setOpen(false);
    }, 1000);
  };

  return (
    <>
      <Button
        appearance="subtle"
        icon={<Settings24Regular />}
        size="small"
        onClick={() => setOpen(true)}
      >
        设置
      </Button>

      {open && (
        <div className={styles.overlay} onClick={() => setOpen(false)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.header}>
              <h3 className={styles.title}>翻译服务设置</h3>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={() => setOpen(false)}
              />
            </div>

            <div className={styles.root}>
              {saved && (
                <MessageBar intent="success">
                  <MessageBarBody>设置已保存</MessageBarBody>
                </MessageBar>
              )}

              <div className={styles.fieldGroup}>
                <label style={labelStyle}>
                  Azure Translator API Key <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="password"
                  style={inputStyle}
                  value={localSettings.apiKey}
                  placeholder="输入你的 Azure Translator 订阅密钥"
                  onChange={(e) =>
                    setLocalSettings((s) => ({ ...s, apiKey: e.target.value }))
                  }
                />
              </div>

              <div className={styles.fieldGroup}>
                <label style={labelStyle}>API 区域</label>
                <select
                  style={selectStyle}
                  value={localSettings.apiRegion}
                  onChange={(e) =>
                    setLocalSettings((s) => ({
                      ...s,
                      apiRegion: e.target.value,
                    }))
                  }
                >
                  <option value="global">Global（全局）</option>
                  <option value="eastasia">East Asia（东亚）</option>
                  <option value="southeastasia">Southeast Asia（东南亚）</option>
                  <option value="westeurope">West Europe（西欧）</option>
                  <option value="eastus">East US（美国东部）</option>
                  <option value="westus">West US（美国西部）</option>
                </select>
              </div>
            </div>

            <div className={styles.actions}>
              <Button appearance="secondary" onClick={() => setOpen(false)}>
                取消
              </Button>
              <Button
                appearance="primary"
                onClick={handleSave}
                disabled={!localSettings.apiKey.trim()}
              >
                保存
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
